/**
 * @fileoverview Sortable Queue Item Row component.
 * @summary Draggable queue item with status management and deletion.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/SortableQueueItemRow
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faGripVertical,
  faPlay,
  faCheck,
  faBan,
  faMicrophone,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { QueueItem, QueueItemStatus } from '../../../store/slices/queueSlice'

/**
 * @summary Get queue item status badge class.
 */
function getQueueStatusBadgeClass(status: QueueItemStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    case 'current':
      return 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
    case 'completed':
      return 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
    case 'cancelled':
      return 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
    default:
      return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  }
}

interface SortableQueueItemRowProps {
  item: QueueItem
  index: number
  onStatusChange: (status: QueueItemStatus) => void
  onDelete: () => void
}

/**
 * @summary Sortable queue item row component with drag-and-drop and status management.
 * @param {SortableQueueItemRowProps} props - Component props.
 * @returns {React.ReactElement} Sortable queue item row.
 */
export function SortableQueueItemRow({
  item,
  index,
  onStatusChange,
  onDelete,
}: SortableQueueItemRowProps) {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  }

  // Check if the item is completed or cancelled (should be grayed out)
  const isInactive = item.status === 'completed' || item.status === 'cancelled'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 bg-(--color-bg-secondary) rounded-lg hover:bg-(--color-bg-tertiary) transition-colors ${isDragging ? 'shadow-lg ring-2 ring-(--color-primary)' : ''} ${isInactive ? 'opacity-50' : ''}`}
    >
      {/* Main row: drag handle, position, song info, status */}
      <div className="flex items-start gap-3">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="shrink-0 p-1 cursor-grab active:cursor-grabbing touch-none"
          title={t('events.dragToReorder')}
        >
          <FontAwesomeIcon icon={faGripVertical} className="w-4 h-4 text-(--color-text-secondary)" />
        </button>

        {/* Position number */}
        <div className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${isInactive ? 'bg-gray-300 text-gray-800' : 'bg-(--color-primary-600) text-white'}`}>
          {index + 1}
        </div>

        {/* Song info - takes remaining space */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className={`font-medium truncate ${isInactive ? 'text-gray-500 line-through' : 'text-(--color-text-primary)'}`} >
            {item.songTitle}
          </div>
          {/* Artist */}
          <div className={`text-sm truncate ${isInactive ? 'text-gray-500' : 'text-(--color-text-secondary)'}`} >
            {item.songArtist}
          </div>
          {/* Singer info */}
          {item.singer && (
            <div className={`text-sm mt-1 flex items-center gap-1 ${isInactive ? 'text-gray-500' : 'text-(--color-text-secondary)'}`}>
              <FontAwesomeIcon icon={faMicrophone} className="w-3 h-3" />
              <span className="font-medium">{item.singer.pseudo}</span>
              {item.singer.guestMode && (
                <span className="text-xs bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-400 px-1.5 py-0.5 rounded">{t('events.guest')}</span>
              )}
            </div>
          )}
        </div>

        {/* Status badge - shrink-0 to prevent wrapping */}
        {item.status && (
          <div className="shrink-0">
            <span className={`inline-block px-2 py-1 text-xs font-medium rounded whitespace-nowrap ${getQueueStatusBadgeClass(item.status)}`}>
              {t(`events.queueStatus${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`)}
            </span>
          </div>
        )}
      </div>

      {/* Actions row - below the main content */}
      <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-(--color-border)">
        {/* Status buttons and delete - hidden if completed or cancelled */}
        {!isInactive && (
          <>
            {item.status !== 'current' && (
              <button
                onClick={() => onStatusChange('current')}
                className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30"
                title={t('events.setCurrent')}
              >
                <FontAwesomeIcon icon={faPlay} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </button>
            )}
            {item.status === 'current' && (
              <button
                onClick={() => onStatusChange('completed')}
                className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30"
                title={t('events.markCompleted')}
              >
                <FontAwesomeIcon icon={faCheck} className="w-4 h-4 text-green-600 dark:text-green-400" />
              </button>
            )}
            {item.status !== 'cancelled' && (
              <button
                onClick={() => onStatusChange('cancelled')}
                className="p-1.5 rounded hover:bg-orange-100 dark:hover:bg-orange-900/30"
                title={t('events.cancel')}
              >
                <FontAwesomeIcon icon={faBan} className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </button>
            )}

            {/* Delete button */}
            <button
              onClick={onDelete}
              className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
              title={t('events.delete')}
            >
              <FontAwesomeIcon icon={faTrash} className="w-4 h-4 text-red-600 dark:text-red-400" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

